<script setup lang="ts">
import type { ChatConnectionStep } from '../../../libs/chat-connection'

import { errorMessageFrom } from '@moeru/std'
import { BasicButton, FieldInput, GhostButton } from '@proj-airi/ui'
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import { useChatConnectionNotice } from '../../../composables/use-chat-connection'
import { sanitizeConnectionError } from '../../../libs/chat-connection'
import { useAiriCardStore } from '../../../stores/modules/airi-card'
import { useConsciousnessStore } from '../../../stores/modules/consciousness'
import { useProviderConfigStore } from '../../../stores/providers/config'
import { useProviderStore } from '../../../stores/providers/provider'

const emit = defineEmits<{
  cancel: []
  saved: []
}>()

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const providersStore = useProviderStore()
const providerConfigStore = useProviderConfigStore()
const cardStore = useAiriCardStore()
const consciousnessStore = useConsciousnessStore()
const { allChatProvidersMetadata } = storeToRefs(providersStore)
const notice = useChatConnectionNotice()

const steps = ['provider', 'configure', 'verify', 'model'] as const
const step = ref<ChatConnectionStep>('provider')
const providerId = ref('')
const apiKey = ref('')
const baseUrl = ref('')
const thinkingMode = ref<'auto' | 'disable' | 'enable'>('auto')
const modelId = ref('')
const models = ref<Array<{ id: string, name: string }>>([])
const formError = ref('')
const verifying = ref(false)
let verifyGeneration = 0
let statusBeforeVerify: 'unconfigured' | 'validating' | 'configured' | 'invalid' | 'bypassed' | undefined

const chatProviders = computed(() => {
  return allChatProvidersMetadata.value.filter(item => !item.id.startsWith('official-provider'))
})

const selectedProvider = computed(() => chatProviders.value.find(item => item.id === providerId.value))
const needsCredentials = computed(() => selectedProvider.value?.requiresCredentials !== false)
const visibleSteps = computed((): ChatConnectionStep[] => {
  if (needsCredentials.value)
    return [...steps]
  return steps.filter(item => item !== 'configure')
})

function stepFromQuery(value: unknown): ChatConnectionStep | undefined {
  if (value === 'provider' || value === 'configure' || value === 'verify' || value === 'model')
    return value
  return undefined
}

function rememberFailure(id: string, error: string) {
  notice.value = {
    providerId: id,
    error: sanitizeConnectionError(error, [apiKey.value, String(providerConfigStore.getProviderConfig(id)?.apiKey ?? '')]),
    checkedAt: new Date().toISOString(),
  }
}

function clearFailure(id: string) {
  if (notice.value?.providerId === id)
    notice.value = null
}

function selectProvider(id: string) {
  providerId.value = id
  const metadata = chatProviders.value.find(item => item.id === id)
  const config = providerConfigStore.getProviderConfig(id) ?? {}
  apiKey.value = ''
  baseUrl.value = typeof config.baseUrl === 'string' && config.baseUrl
    ? config.baseUrl
    : String(metadata?.defaultConfig.baseUrl ?? '')
  const mode = config.thinkingMode
  thinkingMode.value = mode === 'disable' || mode === 'enable' || mode === 'auto' ? mode : 'auto'
  modelId.value = typeof config.model === 'string' ? config.model : ''
  formError.value = ''
}

function goBack() {
  const index = visibleSteps.value.indexOf(step.value)
  if (index <= 0) {
    emit('cancel')
    return
  }
  step.value = visibleSteps.value[index - 1] ?? 'provider'
  formError.value = ''
}

function cancelWizard() {
  verifyGeneration += 1
  verifying.value = false
  emit('cancel')
}

async function continueFromProvider() {
  if (!providerId.value) {
    formError.value = t('settings.pages.chat-connection.wizard.choose-provider')
    return
  }
  formError.value = ''
  step.value = needsCredentials.value ? 'configure' : 'verify'
}

function secretFrom(config: Record<string, unknown>) {
  return typeof config.apiKey === 'string' ? config.apiKey : ''
}

function configToSave() {
  const existingKey = String(providerConfigStore.getProviderConfig(providerId.value)?.apiKey ?? '')
  const next: Record<string, unknown> = {
    apiKey: apiKey.value.trim() || existingKey,
    baseUrl: baseUrl.value.trim(),
  }
  if (providerId.value === 'deepseek')
    next.thinkingMode = thinkingMode.value
  return next
}

async function continueFromConfigure() {
  const config = configToSave()
  if (needsCredentials.value && !config.apiKey) {
    formError.value = t('settings.pages.chat-connection.wizard.missing-key')
    return
  }
  providerConfigStore.ensureProvider(providerId.value, providerId.value, {})
  await providerConfigStore.patchProviderConfig(providerId.value, config)
  formError.value = ''
  step.value = 'verify'
}

function cancelVerification() {
  verifyGeneration += 1
  verifying.value = false
  const provider = providerConfigStore.getProvider(providerId.value)
  if (provider?.status === 'validating')
    providerConfigStore.setProviderStatus(providerId.value, statusBeforeVerify === 'validating' ? 'unconfigured' : statusBeforeVerify ?? 'unconfigured')
}

async function verifyConnection() {
  if (!providerId.value)
    return
  const generation = ++verifyGeneration
  verifying.value = true
  formError.value = ''
  const config = configToSave()
  statusBeforeVerify = providerConfigStore.getProvider(providerId.value)?.status
  providerConfigStore.ensureProvider(providerId.value, providerId.value, {})
  await providerConfigStore.patchProviderConfig(providerId.value, config)
  providerConfigStore.setProviderStatus(providerId.value, 'validating')
  try {
    const result = await providersStore.validateProviderConfig(providerId.value, config)
    if (generation !== verifyGeneration)
      return
    if (!result.valid) {
      providerConfigStore.setProviderStatus(providerId.value, 'invalid')
      const reason = result.reason || t('settings.pages.chat-connection.wizard.verify-failed')
      rememberFailure(providerId.value, reason)
      formError.value = sanitizeConnectionError(reason, [secretFrom(config)])
      return
    }
    providerConfigStore.setProviderStatus(providerId.value, 'configured')
    providerConfigStore.markProviderAdded(providerId.value)
    clearFailure(providerId.value)
    notice.value = {
      providerId: providerId.value,
      error: '',
      checkedAt: new Date().toISOString(),
    }
    const catalog = await providersStore.fetchModelsForProvider(providerId.value)
    models.value = (catalog?.models ?? []).map(model => ({ id: model.id, name: model.name || model.id }))
    if (!modelId.value && models.value[0])
      modelId.value = models.value[0].id
    step.value = 'model'
  }
  catch (error) {
    if (generation !== verifyGeneration)
      return
    providerConfigStore.setProviderStatus(providerId.value, 'invalid')
    const reason = errorMessageFrom(error) ?? t('settings.pages.chat-connection.wizard.verify-failed')
    rememberFailure(providerId.value, reason)
    formError.value = sanitizeConnectionError(reason, [secretFrom(config)])
  }
  finally {
    if (generation === verifyGeneration)
      verifying.value = false
  }
}

async function saveSelection() {
  const model = modelId.value.trim()
  if (!providerId.value || !model) {
    formError.value = t('settings.pages.chat-connection.wizard.missing-model')
    return
  }
  formError.value = ''
  await providerConfigStore.patchProviderConfig(providerId.value, { model })
  await cardStore.updateActiveCardConsciousness({ provider: providerId.value, model })
  emit('saved')
  await router.push('/settings')
}

onMounted(() => {
  const requested = stepFromQuery(route.query.step)
  if (requested)
    step.value = requested
})

watch(chatProviders, (list) => {
  if (providerId.value || list.length === 0)
    return
  const preferred = list.find(item => item.id === consciousnessStore.activeProvider)?.id
    ?? list.find(item => item.id === 'deepseek')?.id
    ?? list[0]?.id
  if (preferred)
    selectProvider(preferred)
}, { immediate: true })
</script>

<template>
  <section :class="['flex flex-col gap-4 rounded-xl bg-neutral-50 p-4 dark:bg-neutral-900/60']">
    <div>
      <h2 :class="['text-lg text-neutral-700 dark:text-neutral-200']">
        {{ t('settings.pages.chat-connection.wizard.title') }}
      </h2>
      <p :class="['text-sm text-neutral-500']">
        {{ t(`settings.pages.chat-connection.wizard.step.${step}`) }}
      </p>
    </div>

    <div v-if="step === 'provider'" role="list" :class="['flex flex-col gap-2']">
      <button
        v-for="provider in chatProviders"
        :key="provider.id"
        type="button"
        role="listitem"
        :aria-pressed="provider.id === providerId"
        :class="[
          'rounded-lg border px-3 py-2 text-left',
          provider.id === providerId
            ? 'border-primary-500 bg-primary-50 dark:bg-primary-950/40'
            : 'border-neutral-200 bg-white dark:border-neutral-700 dark:bg-neutral-900',
        ]"
        @click="selectProvider(provider.id)"
      >
        <span :class="['block text-sm font-medium text-neutral-800 dark:text-neutral-100']">
          {{ provider.localizedName }}
        </span>
        <span :class="['block text-xs text-neutral-500']">{{ provider.localizedDescription }}</span>
      </button>
    </div>

    <div v-else-if="step === 'configure'" :class="['flex flex-col gap-3']">
      <FieldInput
        v-if="needsCredentials"
        v-model="apiKey"
        type="password"
        autocomplete="off"
        :label="t('settings.dialogs.onboarding.apiKey')"
        :placeholder="t('settings.pages.chat-connection.wizard.key-placeholder')"
      />
      <FieldInput
        v-model="baseUrl"
        :label="t('settings.dialogs.onboarding.baseUrl')"
        placeholder="https://api.deepseek.com/"
      />
      <fieldset v-if="providerId === 'deepseek'" :class="['flex flex-col gap-2']">
        <legend :class="['text-sm text-neutral-600 dark:text-neutral-300']">
          {{ t('settings.pages.chat-connection.wizard.thinking-mode') }}
        </legend>
        <div :class="['flex flex-wrap gap-2']">
          <BasicButton
            v-for="mode in (['auto', 'disable', 'enable'] as const)"
            :key="mode"
            size="sm"
            type="button"
            :aria-pressed="thinkingMode === mode"
            @click="thinkingMode = mode"
          >
            {{ t(`settings.pages.providers.catalog.edit.config.common.fields.field.thinking-mode.options.${mode}`) }}
          </BasicButton>
        </div>
      </fieldset>
    </div>

    <div v-else-if="step === 'verify'" :class="['flex flex-col gap-3']">
      <p :class="['text-sm text-neutral-600 dark:text-neutral-300']">
        {{ selectedProvider?.localizedName }}
      </p>
      <BasicButton type="button" :loading="verifying" :disabled="verifying" @click="verifyConnection">
        {{ t('settings.pages.chat-connection.wizard.verify') }}
      </BasicButton>
      <GhostButton v-if="verifying" type="button" @click="cancelVerification">
        {{ t('settings.pages.chat-connection.action.cancel-verification') }}
      </GhostButton>
    </div>

    <div v-else :class="['flex flex-col gap-3']">
      <div v-if="models.length > 0" role="list" :class="['flex flex-col gap-2']">
        <button
          v-for="model in models"
          :key="model.id"
          type="button"
          role="listitem"
          :aria-pressed="model.id === modelId"
          :class="[
            'rounded-lg border px-3 py-2 text-left text-sm',
            model.id === modelId
              ? 'border-primary-500 bg-primary-50 dark:bg-primary-950/40'
              : 'border-neutral-200 bg-white dark:border-neutral-700 dark:bg-neutral-900',
          ]"
          @click="modelId = model.id"
        >
          {{ model.name }}
        </button>
      </div>
      <FieldInput
        v-model="modelId"
        :label="t('settings.pages.chat-connection.wizard.model-id')"
        placeholder="deepseek-flash"
      />
    </div>

    <p v-if="formError" role="alert" :class="['text-sm text-red-700 dark:text-red-300']">
      {{ formError }}
    </p>

    <div :class="['flex flex-wrap gap-2']">
      <GhostButton type="button" @click="goBack">
        {{ t('settings.pages.chat-connection.wizard.back') }}
      </GhostButton>
      <GhostButton type="button" @click="cancelWizard">
        {{ t('settings.pages.chat-connection.wizard.cancel') }}
      </GhostButton>
      <BasicButton v-if="step === 'provider'" type="button" @click="continueFromProvider">
        {{ t('settings.pages.chat-connection.wizard.next') }}
      </BasicButton>
      <BasicButton v-else-if="step === 'configure'" type="button" @click="continueFromConfigure">
        {{ t('settings.pages.chat-connection.wizard.next') }}
      </BasicButton>
      <BasicButton v-else-if="step === 'model'" type="button" @click="saveSelection">
        {{ t('settings.pages.chat-connection.wizard.save') }}
      </BasicButton>
    </div>
  </section>
</template>
